import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Package, Users, MessageCircle, CheckCircle, Calendar } from 'lucide-react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { SEO } from '../components/SEO';
import { useCampaignDetail } from '../hooks/useCampaignDetail';
import { CampaignDetailHeader } from '../components/campaign/CampaignDetailHeader';
import { ProductList } from '../components/campaign/ProductList';
import { ContactInfo } from '../components/campaign/ContactInfo';
import { DonationFormModal } from '../components/campaign/DonationFormModal';
import { CommentSection } from '../components/campaign/CommentSection';
import { CommitmentsTable } from '../components/campaign/CommitmentsTable';
import { ShareModal } from '../components/ShareModal';
import { ResultsSection } from '../components/campaign/ResultsSection';
import { MediaCarousel } from '../components/campaign/MediaCarousel';
import { CompleteCampaignModal } from '../components/campaign/CompleteCampaignModal';
import { ReportCampaignModal } from '../components/campaign/ReportCampaignModal';
import { formatDate } from '../utils/date';
import { apiUrl } from '../utils/api';

interface CommitmentItem {
  productId: string;
  quantity: number;
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    campaign, products, commitments, setCommitments,
    comments, setComments, ownerProfile, isLoading, error, setError,
    currentUser, isOwner, reload,
  } = useCampaignDetail(id);

  const [activeTab, setActiveTab] = useState<'details' | 'commitments' | 'forum' | 'results'>('details');
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [donationItems, setDonationItems] = useState<CommitmentItem[]>([]);
  const [media, setMedia] = useState<{ id: string; mediaType: 'image' | 'video'; url: string; caption?: string }[]>([]);

  // Load campaign media
  useEffect(() => {
    if (!id) return;
    fetch(apiUrl(`/api/campaigns/${id}/media`))
      .then(res => res.json())
      .then(json => { if (json.success) setMedia(json.data); })
      .catch(() => {});
  }, [id]);

  const handleDonateProduct = (productId: string, maxQuantity: number) => {
    const existing = donationItems.find(i => i.productId === productId);
    if (existing) {
      setDonationItems(donationItems.map(i =>
        i.productId === productId ? { ...i, quantity: Math.min(i.quantity + 1, maxQuantity) } : i
      ));
    } else {
      setDonationItems([...donationItems, { productId, quantity: 1 }]);
    }
    setShowDonationForm(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Cargando campaña...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-sm mx-auto p-6">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Campaña no encontrada</h2>
            <p className="text-sm text-gray-600 mb-6">{error}</p>
            <div className="space-y-2">
              <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700">
                Volver al inicio
              </button>
              <button onClick={() => window.location.reload()} className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-200">
                Recargar
              </button>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SEO
        title={campaign.title}
        description={campaign.description?.slice(0, 160) || `Campaña de donación en ${campaign.location?.city || 'DonaApp'}`}
        url={`https://donaapp.co/campaigns/${campaign.id}`}
      />
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Nav header with logo */}
        <nav className="bg-white border-b sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/isotipo.png" alt="DonaApp" className="h-8 w-8" />
              <img src="/logotipo.png" alt="DonaApp" className="h-5 hidden sm:block" />
            </Link>
            <div className="flex items-center space-x-3 text-sm">
              {currentUser ? (
                <>
                  <Link to="/profile" className="text-gray-600 hover:text-indigo-600 font-medium">Perfil</Link>
                  <Link to="/dashboard" className="text-gray-600 hover:text-indigo-600 font-medium">Dashboard</Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-600 hover:text-indigo-600 font-medium">Iniciar Sesión</Link>
                  <Link to="/register" className="px-3 py-1.5 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 font-medium">Registrarse</Link>
                </>
              )}
            </div>
          </div>
        </nav>

        <main className="flex-1">
          <CampaignDetailHeader
            campaign={campaign}
            isOwner={!!isOwner}
            onDonateClick={() => setShowDonationForm(true)}
            onShareClick={() => setShowShareModal(true)}
            onCompleteClick={() => setShowCompleteModal(true)}
            onReportClick={currentUser && !isOwner ? () => setShowReportModal(true) : undefined}
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Media Carousel */}
            {media.length > 0 && <MediaCarousel media={media} />}

            {/* Description */}
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{campaign.description}</p>
            </div>

            {/* Banner: Expired without closure */}
            {campaign.status === 'expired' && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      ⚠️ Esta campaña venció el {formatDate(campaign.deadline)} y el organizador aún no ha confirmado el cierre ni compartido evidencia de las donaciones recibidas.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Banner: 100% progress reached (owner version) */}
            {isOwner && campaign.status === 'active' && products.length > 0 && products.every(p => p.isFullyCommitted) && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-indigo-900">
                      ¡Llegaste al 100%! Cuando hayas completado las entregas, cierra tu campaña y comparte la evidencia con tus donantes.
                    </p>
                    <button
                      onClick={() => setShowCompleteModal(true)}
                      className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Finalizar campaña
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Banner: 100% progress reached (public version) */}
            {!isOwner && campaign.status === 'active' && products.length > 0 && products.every(p => p.isFullyCommitted) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium text-green-900">
                    Meta alcanzada — el organizador está finalizando el proceso de entrega de las donaciones.
                  </p>
                </div>
              </div>
            )}

            {/* Completion message */}
            {campaign.status === 'completed' && campaign.completionMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-green-900 mb-1">¡Campaña Completada!</h3>
                    <p className="text-sm text-green-800 italic">"{campaign.completionMessage}"</p>
                    {campaign.completionDate && (
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(campaign.completionDate)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex items-center space-x-1 mb-6 bg-white rounded-lg border border-gray-200 p-1 overflow-x-auto">
              <button onClick={() => setActiveTab('details')}
                className={`flex items-center justify-center space-x-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-1 ${
                  activeTab === 'details' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}>
                <Package className="w-4 h-4" />
                <span>Detalles</span>
              </button>
              {isOwner && (
                <button onClick={() => setActiveTab('commitments')}
                  className={`flex items-center justify-center space-x-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-1 ${
                    activeTab === 'commitments' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}>
                  <Users className="w-4 h-4" />
                  <span>Donantes{commitments.length > 0 ? ` (${commitments.length})` : ''}</span>
                </button>
              )}
              <button onClick={() => setActiveTab('forum')}
                className={`flex items-center justify-center space-x-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-1 ${
                  activeTab === 'forum' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}>
                <MessageCircle className="w-4 h-4" />
                <span>Chat{comments.length > 0 ? ` (${comments.length})` : ''}</span>
              </button>
              {campaign.status === 'completed' && (
                <button onClick={() => setActiveTab('results')}
                  className={`flex items-center justify-center space-x-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-1 ${
                    activeTab === 'results' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}>
                  <CheckCircle className="w-4 h-4" />
                  <span>Resultados</span>
                </button>
              )}
            </div>

            {/* Tab content */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                <ProductList
                  products={products}
                  campaign={campaign}
                  isOwner={!!isOwner}
                  onDonateProduct={handleDonateProduct}
                />
                <ContactInfo campaign={campaign} ownerProfile={ownerProfile} />
              </div>
            )}

            {activeTab === 'commitments' && isOwner && (
              <CommitmentsTable
                commitments={commitments}
                setCommitments={setCommitments}
                campaignTitle={campaign.title}
                onReload={reload}
                onError={setError}
              />
            )}

            {activeTab === 'forum' && (
              <CommentSection
                campaignId={campaign.id}
                comments={comments}
                setComments={setComments}
                currentUser={currentUser}
              />
            )}

            {activeTab === 'results' && campaign.status === 'completed' && (
              <ResultsSection campaignId={campaign.id} isOwner={!!isOwner} />
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <img src="/isotipo.png" alt="DonaApp" className="h-8 w-8" />
                  <span className="text-white font-semibold text-lg">DonaApp</span>
                </div>
                <p className="text-sm text-gray-400">Donaciones en especie para causas verificadas.</p>
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-3">Información Legal</h4>
                <ul className="space-y-1 text-sm text-gray-400">
                  <li className="font-medium text-gray-300">OVIEDOTECH S.A.S.</li>
                  <li>NIT: 901.964.524-2</li>
                  <li>Cali, Valle del Cauca, Colombia</li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-3">Contacto</h4>
                <ul className="space-y-1 text-sm text-gray-400">
                  <li><a href="mailto:contacto@donaapp.co" className="hover:text-indigo-400 transition-colors">contacto@donaapp.co</a></li>
                  <li><a href="tel:+573169277199" className="hover:text-indigo-400 transition-colors">+57 316 927 7199</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-3">Enlaces</h4>
                <ul className="space-y-1 text-sm">
                  <li><Link to="/marketplace" className="text-gray-400 hover:text-indigo-400 transition-colors">Marketplace</Link></li>
                  <li><Link to="/acerca-de" className="text-gray-400 hover:text-indigo-400 transition-colors">Acerca de</Link></li>
                  <li><Link to="/faq" className="text-gray-400 hover:text-indigo-400 transition-colors">FAQ</Link></li>
                  <li><Link to="/terminos-y-condiciones" className="text-gray-400 hover:text-indigo-400 transition-colors">Términos</Link></li>
                  <li><Link to="/politica-de-privacidad" className="text-gray-400 hover:text-indigo-400 transition-colors">Privacidad</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-5 text-center text-xs text-gray-500">
              © {new Date().getFullYear()} DonaApp — OVIEDOTECH S.A.S. Todos los derechos reservados.
            </div>
          </div>
        </footer>

        {/* Donation modal */}
        {showDonationForm && (
          <DonationFormModal
            campaign={campaign}
            products={products}
            initialItems={donationItems}
            onClose={() => { setShowDonationForm(false); setDonationItems([]); }}
            onSuccess={() => { setShowDonationForm(false); setDonationItems([]); reload(); }}
            onError={setError}
          />
        )}

        {/* Share modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          campaignId={campaign.id}
          campaignTitle={campaign.title}
        />

        {/* Complete campaign modal */}
        <CompleteCampaignModal
          isOpen={showCompleteModal}
          campaignId={campaign.id}
          campaignTitle={campaign.title}
          commitments={commitments}
          onClose={() => setShowCompleteModal(false)}
          onSuccess={() => {
            setShowCompleteModal(false);
            reload();
            setActiveTab('results');
          }}
        />

        {/* Report campaign modal */}
        <ReportCampaignModal
          isOpen={showReportModal}
          campaignId={campaign.id}
          onClose={() => setShowReportModal(false)}
        />
      </div>
    </ErrorBoundary>
  );
}
